import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import API from "../services/api";
import Loader from "../components/Loader";
import { FaEdit, FaEye, FaPlus, FaSearch } from "react-icons/fa";

function Departments() {
    const [departments, setDepartments] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [form, setForm] = useState({
        name: "",
        designation: ""
    });
    const [loading, setLoading] = useState(true);

    // Pagination State
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 5;

    // =====================================
    // GET DEPARTMENTS
    // =====================================
    const getDepartments = async (currentPage = 1) => {
        try {
            setLoading(true);
            const response = await API.get(`/department/list?page=${currentPage}&limit=${limit}`);
            setDepartments(response.data.departments);
            setTotalPages(response.data.totalPages || 1);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getDepartments(page);
    }, [page]);

    // =====================================
    // INPUT CHANGE
    // =====================================
    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value
        });
    };

    // =====================================
    // ADD DEPARTMENT
    // =====================================
    const addDepartment = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.designation.trim()) {
            alert("Please fill in all fields.");
            return;
        }

        try {
            await API.post("/department/add", form);
            alert("Department Added");
            setShowAddModal(false);
            setForm({
                name: "",
                designation: ""
            });
            getDepartments(page);
        } catch (error) {
            alert(error.response?.data?.message || "Failed to add department.");
        }
    };

    // =====================================
    // EDIT DEPARTMENT
    // =====================================
    const editDepartment = async (e) => {
        e.preventDefault();
        try {
            await API.put(
                `/department/update/${selectedDepartment._id}`,
                form
            );
            alert("Department Updated");
            setShowEditModal(false);
            getDepartments(page);
            setForm({
                name: "",
                designation: ""
            });
        } catch (error) {
            console.log(error);
        }
    };

    // =====================================
    // OPEN EDIT MODAL
    // =====================================
    const openEditModal = (dep) => {
        setSelectedDepartment(dep);
        setForm({
            name: dep.name,
            designation: dep.designation
        });
        setShowEditModal(true);
    };

    // =====================================
    // FILTER (Client-side filter on current page)
    // =====================================
    const filteredDepartments = departments.filter((dep) =>
        dep.name.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="main-wrapper">
            <Sidebar />

            <div className="content-wrapper">
                {/* HEADER */}
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-4 animate-fade-in">
                    <div>
                        <h2 className="fw-extrabold text-slate mb-1">Departments</h2>
                        <p className="text-muted mb-0">Create and manage agency departments and roles</p>
                    </div>

                    <button
                        className="btn btn-primary d-flex align-items-center gap-2 shadow-sm"
                        onClick={() => {
                            setForm({ name: "", designation: "" });
                            setShowAddModal(true);
                        }}
                    >
                        <FaPlus size={14} /> Add Department
                    </button>
                </div>

                {/* SEARCH */}
                <div className="card border-0 shadow-sm mb-4 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                    <div className="card-body p-3">
                        <div className="input-group">
                            <span className="input-group-text bg-transparent border-0 pe-0" style={{ color: "#94a3b8" }}>
                                <FaSearch />
                            </span>
                            <input
                                type="text"
                                placeholder="Search departments on this page..."
                                className="form-control border-0 shadow-none bg-transparent"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <Loader message="Loading departments..." />
                ) : (
                    <>
                        {/* TABLE */}
                        <div className="card border-0 shadow-sm animate-fade-in" style={{ animationDelay: "0.2s" }}>
                            <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-4" style={{ width: "80px" }}>#</th>
                                        <th>Department Name</th>
                                        <th>Designation / Role Details</th>
                                        <th className="pe-4 text-end" style={{ width: "120px" }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDepartments.length > 0 ? (
                                        filteredDepartments.map((dep, index) => (
                                            <tr key={dep._id} className="hover-bg-light">
                                                <td className="ps-4 text-muted font-monospace">
                                                    {((page - 1) * limit) + index + 1}
                                                </td>
                                                <td>
                                                    <span className="fw-bold text-dark">
                                                        {dep.name}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-muted">{dep.designation}</span>
                                                </td>
                                                <td className="pe-4 text-end">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button
                                                            className="btn btn-sm d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: "32px",
                                                                height: "32px",
                                                                padding: "0",
                                                                borderRadius: "8px",
                                                                background: "rgba(14, 165, 233, 0.1)",
                                                                color: "#0ea5e9",
                                                                border: "none"
                                                            }}
                                                            onClick={() => {
                                                                setSelectedDepartment(dep);
                                                                setShowViewModal(true);
                                                            }}
                                                            title="View Department"
                                                        >
                                                            <FaEye size={14} />
                                                        </button>

                                                        <button
                                                            className="btn btn-sm d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: "32px",
                                                                height: "32px",
                                                                padding: "0",
                                                                borderRadius: "8px",
                                                                background: "rgba(245, 158, 11, 0.1)",
                                                                color: "#f59e0b",
                                                                border: "none"
                                                            }}
                                                            onClick={() => openEditModal(dep)}
                                                            title="Edit Department"
                                                        >
                                                            <FaEdit size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-center py-5 text-muted">
                                                <h5>No Departments Found</h5>
                                                <p className="small mb-0">Get started by clicking Add Department.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* PAGINATION */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-center align-items-center gap-2 mt-4 animate-fade-in">
                        {[...Array(totalPages)].map((_, index) => (
                            <button
                                key={index}
                                className={`btn d-flex align-items-center justify-content-center font-semibold`}
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    padding: "0",
                                    borderRadius: "50%",
                                    backgroundColor: page === index + 1 ? "var(--primary-color)" : "#ffffff",
                                    color: page === index + 1 ? "#ffffff" : "var(--text-muted)",
                                    border: page === index + 1 ? "none" : "1px solid var(--border-light)",
                                    boxShadow: page === index + 1 ? "0 4px 10px rgba(79, 70, 229, 0.2)" : "none"
                                }}
                                onClick={() => setPage(index + 1)}
                            >
                                {index + 1}
                            </button>
                        ))}
                    </div>
                )}
                    </>
                )}

                {/* VIEW MODAL */}
                {showViewModal && (
                    <div 
                        className="modal d-flex align-items-center justify-content-center position-fixed top-0 start-0 w-100 h-100" 
                        style={{ 
                            backgroundColor: "rgba(15, 23, 42, 0.4)", 
                            backdropFilter: "blur(4px)",
                            zIndex: 1060 
                        }}
                    >
                        <div className="modal-dialog w-100" style={{ maxWidth: "500px" }}>
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "16px" }}>
                                <div className="modal-header border-bottom-0 pb-0">
                                    <h5 className="fw-bold mb-0">Department Details</h5>
                                    <button
                                        className="btn-close shadow-none"
                                        onClick={() => setShowViewModal(false)}
                                    />
                                </div>
                                <div className="modal-body py-4">
                                    <div className="mb-3">
                                        <label className="text-muted small fw-semibold text-uppercase">Department Name</label>
                                        <div className="fs-5 fw-bold text-dark mt-1">{selectedDepartment?.name}</div>
                                    </div>
                                    <div>
                                        <label className="text-muted small fw-semibold text-uppercase">Designation Details</label>
                                        <div className="text-slate mt-1">{selectedDepartment?.designation || "N/A"}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ADD MODAL */}
                {showAddModal && (
                    <div 
                        className="modal d-flex align-items-center justify-content-center position-fixed top-0 start-0 w-100 h-100" 
                        style={{ 
                            backgroundColor: "rgba(15, 23, 42, 0.4)", 
                            backdropFilter: "blur(4px)",
                            zIndex: 1060 
                        }}
                    >
                        <div className="modal-dialog w-100" style={{ maxWidth: "500px" }}>
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "16px" }}>
                                <div className="modal-header border-bottom-0">
                                    <h5 className="fw-bold mb-0">Add Department</h5>
                                    <button
                                        className="btn-close shadow-none"
                                        onClick={() => setShowAddModal(false)}
                                    />
                                </div>
                                <form onSubmit={addDepartment}>
                                    <div className="modal-body py-3">
                                        <div className="mb-3">
                                            <label className="form-label text-muted small fw-semibold">Department Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                placeholder="e.g., Cyber Security"
                                                className="form-control"
                                                value={form.name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label text-muted small fw-semibold">Designation / Role Description *</label>
                                            <input
                                                type="text"
                                                name="designation"
                                                placeholder="e.g., Senior Officer"
                                                className="form-control"
                                                value={form.designation}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="modal-footer border-top-0 pt-0">
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            onClick={() => setShowAddModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary shadow-sm"
                                        >
                                            Save Department
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* EDIT MODAL */}
                {showEditModal && (
                    <div 
                        className="modal d-flex align-items-center justify-content-center position-fixed top-0 start-0 w-100 h-100" 
                        style={{ 
                            backgroundColor: "rgba(15, 23, 42, 0.4)", 
                            backdropFilter: "blur(4px)",
                            zIndex: 1060 
                        }}
                    >
                        <div className="modal-dialog w-100" style={{ maxWidth: "500px" }}>
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "16px" }}>
                                <div className="modal-header border-bottom-0">
                                    <h5 className="fw-bold mb-0">Edit Department</h5>
                                    <button
                                        className="btn-close shadow-none"
                                        onClick={() => setShowEditModal(false)}
                                    />
                                </div>
                                <form onSubmit={editDepartment}>
                                    <div className="modal-body py-3">
                                        <div className="mb-3">
                                            <label className="form-label text-muted small fw-semibold">Department Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                className="form-control"
                                                value={form.name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label text-muted small fw-semibold">Designation / Role Description *</label>
                                            <input
                                                type="text"
                                                name="designation"
                                                className="form-control"
                                                value={form.designation}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="modal-footer border-top-0 pt-0">
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            onClick={() => setShowEditModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary shadow-sm"
                                        >
                                            Update Details
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Departments;